import {useEffect, useRef, useState, type ReactNode} from 'react';
import type {TweChatMessage} from '../constructor/assistantModel';
import { ADVISOR_ICON } from './AdvisorDialog';
import { hasValidRefinement } from './FollowUpRefinement';
import {resolveAgentContent} from '../content';

type AssistantMessage = TweChatMessage;

type PresentationPhase = 'curating' | 'finishing' | 'leaving' | 'answer' | 'idle';
type StatusMotion = 'visible' | 'leaving' | 'entering';

export function hasMeaningfulContent(message: AssistantMessage) {
  return Boolean(message.text?.trim() || message.groups?.length || hasValidRefinement(message.refinement) || message.followUpQuestions?.length);
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Presentation only: the SDK message, events and persisted transcript are untouched. */
export function AssistantContent({message, children}: {message: AssistantMessage; children: ReactNode}) {
  const responseContent = resolveAgentContent().response;
  const curationMessages = responseContent.curationMessages;
  const meaningful = hasMeaningfulContent(message);
  const pending = !meaningful && (message.status === 'loading' || message.status === 'streaming');
  const [phase, setPhase] = useState<PresentationPhase>(() => pending ? 'curating' : meaningful ? 'answer' : 'idle');
  const [statusIndex, setStatusIndex] = useState(0);
  const [statusMotion, setStatusMotion] = useState<StatusMotion>('visible');
  const handoffStarted = useRef(false);

  useEffect(() => {
    if (pending) {
      handoffStarted.current = false;
      setPhase('curating');
      return;
    }
    if (!meaningful) {
      setPhase('idle');
      return;
    }
    if (reducedMotion()) {
      setPhase('answer');
      return;
    }
    if (phase !== 'curating' || handoffStarted.current) return;

    // Let the active curation message settle, complete one final star glint,
    // then crossfade into the streamed response without moving the turn.
    handoffStarted.current = true;
    setPhase('finishing');
    const leave = window.setTimeout(() => setPhase('leaving'), 400);
    const reveal = window.setTimeout(() => setPhase('answer'), 780);
    return () => {window.clearTimeout(leave); window.clearTimeout(reveal);};
  }, [meaningful, pending]);

  useEffect(() => {
    if (phase !== 'curating') return;
    let timer: number;
    const rotateStatus = () => {
      setStatusMotion('leaving');
      timer = window.setTimeout(() => {
        setStatusIndex(current => (current + 1) % curationMessages.length);
        setStatusMotion('entering');
        timer = window.setTimeout(() => {
          setStatusMotion('visible');
          timer = window.setTimeout(rotateStatus, 1300);
        }, 300);
      }, 200);
    };
    setStatusMotion('visible');
    timer = window.setTimeout(rotateStatus, 1300);
    return () => window.clearTimeout(timer);
  }, [phase, curationMessages.length]);

  const showCuration = phase === 'curating' || phase === 'finishing' || phase === 'leaving';
  const showAnswer = meaningful && (phase === 'leaving' || phase === 'answer');
  if (!showCuration && !showAnswer) return null;

  return <div className="twe-drinks-advisor__answer-body" data-presentation={phase}>
    {showCuration && <>
      <p className="twe-drinks-advisor__sr-only" role="status">{responseContent.preparationStatus}</p>
      <div className={`twe-drinks-advisor__answer-placeholder${phase === 'leaving' ? ' twe-drinks-advisor__answer-placeholder--leaving' : ''}${phase === 'finishing' ? ' twe-drinks-advisor__answer-placeholder--finishing' : ''}`} aria-hidden="true">
        <div className="twe-drinks-advisor__curation">
          <div className="twe-drinks-advisor__curation-mark">
            <span className="twe-drinks-advisor__curation-ring twe-drinks-advisor__curation-ring--one"/>
            <span className="twe-drinks-advisor__curation-ring twe-drinks-advisor__curation-ring--two"/>
            <img className="twe-drinks-advisor__curation-logo" src={ADVISOR_ICON} alt="" width="45" height="54"/>
            <span className="twe-drinks-advisor__curation-glint"/>
          </div>
          <div className="twe-drinks-advisor__curation-copy">
            <p className="twe-drinks-advisor__thinking"><span className={`twe-drinks-advisor__thinking-message twe-drinks-advisor__thinking-message--${statusMotion}`}>{curationMessages[statusIndex]}</span></p>
          </div>
        </div>
        <div className="twe-drinks-advisor__curation-preview" aria-hidden="true">
          <span><i className="twe-drinks-advisor__curation-bottle"/></span>
          <span><i className="twe-drinks-advisor__curation-bottle"/></span>
          <span><i className="twe-drinks-advisor__curation-bottle"/></span>
          <span><i className="twe-drinks-advisor__curation-bottle"/></span>
        </div>
      </div>
    </>}
    {showAnswer && <div className="twe-drinks-advisor__answer-content">{children}</div>}
    {message.status === 'done' && showAnswer && <p className="twe-drinks-advisor__sr-only" aria-live="polite" aria-atomic="true">{responseContent.completedAnnouncement}</p>}
  </div>;
}
